import { FC, useEffect, useMemo, useRef, useTransition } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import {
  useGlobalEffectActions,
  useRideNearbyActions,
  useRidesNavigation,
} from "@ryde/redux";
import {
  loadersEditFormDataSF,
  Log,
  nearbyFilteringRideIdAtom,
  nearbyOtherRidesIdsBatchAtom,
  onActivateKeyDown,
  RideCrudModeKey,
} from "@ryde/shared";

export const RideEditTopInfoNearBy: FC<{ rideId: number }> = ({ rideId }) => {
  const { t } = useTranslation();
  const [isPending, startTransition] = useTransition();
  const { fetchNearbyRidesBatch } = useRideNearbyActions();
  const nearbyOtherRidesIdsBatch = useRecoilValue(nearbyOtherRidesIdsBatchAtom);
  const setFilteringRideId = useSetRecoilState(nearbyFilteringRideIdAtom);
  const { resetPaginationAppEvent } = useGlobalEffectActions();
  const isEditFormDataLoading = useRecoilValue(
    loadersEditFormDataSF(RideCrudModeKey.uiUnit),
  );
  const { navigateRidesGridMultiSelect } = useRidesNavigation();

  const abortControllerRef = useRef<AbortController | undefined>(undefined);

  const filteredRidesIdsByRideId = useMemo(
    () => nearbyOtherRidesIdsBatch.data[rideId],
    [nearbyOtherRidesIdsBatch.data, rideId],
  );

  const isNearByLoading = useMemo(
    () => nearbyOtherRidesIdsBatch?.isLoading,
    [nearbyOtherRidesIdsBatch],
  );

  const ridesNearByIdsDisplay = useMemo(() => {
    const maxDisplayCount = 4;

    if (filteredRidesIdsByRideId?.length) {
      const isHasMoreThanVisible =
        filteredRidesIdsByRideId?.length > maxDisplayCount;

      const visibleNearByIds = isHasMoreThanVisible
        ? filteredRidesIdsByRideId.slice(0, maxDisplayCount)
        : filteredRidesIdsByRideId;
      return `${visibleNearByIds.join(", ")}${isHasMoreThanVisible ? "..." : ""}`;
    }

    return "";
  }, [filteredRidesIdsByRideId]);

  const hasNearby = useMemo(() => {
    return !isNearByLoading && Boolean(filteredRidesIdsByRideId?.length);
  }, [filteredRidesIdsByRideId, isNearByLoading]);

  const handleNearbyClick = useEvent((): void => {
    if (rideId) {
      setFilteringRideId(rideId);
      resetPaginationAppEvent();
      navigateRidesGridMultiSelect(false);
    }
  });

  useEffect(() => {
    abortControllerRef.current = new AbortController();

    return () => {
      if (abortControllerRef.current && isPending) {
        Log.debug("Nearby ride fetch aborted in CARD for ride id", rideId);
        abortControllerRef.current.abort();
      }
    };
  }, [isPending, rideId]);

  useEffect(() => {
    const fetchRides = async (id: number): Promise<void> => {
      startTransition(() => {
        fetchNearbyRidesBatch([id]);
      });
    };

    if (
      rideId &&
      !isEditFormDataLoading &&
      filteredRidesIdsByRideId === undefined &&
      !isNearByLoading
    ) {
      fetchRides(rideId);
    }
  }, [
    fetchNearbyRidesBatch,
    filteredRidesIdsByRideId,
    isEditFormDataLoading,
    isNearByLoading,
    rideId,
  ]);

  return (
    hasNearby && (
      <div className="info_item">
        <p>
          <SVG_MUI.FunnelHasNearbyOrange4SVG className="info_icon nearby_funnel_icon" />
          {t(`ride.OTHER_RIDES_NEAR_BY_DETECTED`, {
            rideIdsJoin: ridesNearByIdsDisplay,
          })}
          {" - "}
          <span
            className="pointer display-near-by-link"
            role="button"
            tabIndex={0}
            onClick={handleNearbyClick}
            onKeyDown={onActivateKeyDown(handleNearbyClick)}
          >
            {t(`general.DISPLAY`)}
          </span>
        </p>
      </div>
    )
  );
};
